import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";

var _dec, _class, _class2;

import { clone, getParent, model, Model, modelAction, prop } from "mobx-keystone";
import { ActionScope, ReceiveQuestActionModel, SetTagActionModel, SetVariableActionModel, TreeParamterActionModel, TreeEnterActionModel, TreeExitActionModel, TreePropertiesActionModel, ReceiveTaskActionModel, CalculateVariableActionModel, InteractionTriggerActionModel, CopyAwarenessIntoVariableActionModel, LocationTriggerActionModel, AbortQuestActionModel, FinishQuestActionModel, FinishTaskActionModel, AbortTaskActionModel } from "./ActionModel";
import { ActionPositionModel } from "./ActionPositionModel";
import { ActionTreeColors } from "../data/ActionTreeColors";
import { EditorComplexity } from "../definitions/other/EditorComplexity";
import { computed, makeObservable, observable } from "mobx";
import { QuestIdValueModel } from "./ValueModel";
export const subTreeNodeSize = {
  width: 200,
  height: 120
};
export let ActionTreeType;

(function (ActionTreeType) {
  ActionTreeType[ActionTreeType["SubTree"] = 0] = "SubTree";
  ActionTreeType[ActionTreeType["MainGameRoot"] = 1] = "MainGameRoot";
  ActionTreeType[ActionTreeType["TemplateRoot"] = 2] = "TemplateRoot";
  ActionTreeType[ActionTreeType["ModuleRoot"] = 3] = "ModuleRoot";
})(ActionTreeType || (ActionTreeType = {}));

export let ActionTreeModel = (_dec = model("actions/ActionTreeModel"), _dec(_class = (_class2 = class ActionTreeModel extends Model({
  position: prop(() => new ActionPositionModel({})),
  comment: prop("").withSetter(),
  nonSubTreeActions: prop(() => [new TreePropertiesActionModel({}), new TreeEnterActionModel({
    position: new ActionPositionModel({
      y: 40
    })
  }), new TreeExitActionModel({
    position: new ActionPositionModel({
      x: 200 / 0.6,
      y: 40
    })
  })]),
  type: prop(ActionTreeType.SubTree).withSetter(),
  parentModelId: prop(null).withSetter(),
  startAtAct: prop(null).withSetter()
}) {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "temporaryParentModelId", void 0);

    _defineProperty(this, "treeAccess", {
      getSubTreesForActionTree: () => {
        throw Error("Please set getSubTreesForActionTreeCallback for this ActionTreeModel before accessing allActions or subTreeActions.");
      },
      getTreeById: () => {
        throw Error("Please set getTreeById for this ActionTreeModel before using it.");
      }
    });

    _defineProperty(this, "isActionModel", true);

    _defineProperty(this, "minimumComplexity", EditorComplexity.Production);

    _defineProperty(this, "deactivationGroupId", "");
  }

  onInit() {
    makeObservable(this, {
      treeAccess: observable
    });
  }

  get activeParentModelId() {
    return this.temporaryParentModelId || this.parentModelId;
  }

  static createEmptyPrototype() {
    const prototype = new ActionTreeModel({});
    prototype.treeAccess = {
      getSubTreesForActionTree: () => [],
      getTreeById: () => null
    };
    return prototype;
  }

  shortDescription(_rootActionTrees, _t, languageKey) {
    var _this$treePropertiesA;

    return (_this$treePropertiesA = this.treePropertiesAction) === null || _this$treePropertiesA === void 0 ? void 0 : _this$treePropertiesA.localizedName.get(languageKey);
  }

  exits() {
    return this.exitActions.map(a => a.subTreeExit);
  }

  exitLabels(t, languageKey) {
    return this.exitActions.map(a => a.name.get(languageKey));
  }

  title() {
    return "action_editor.node_subtree";
  }

  translatableEntityData() {
    return null;
  }

  nodeColor() {
    return ActionTreeColors.ACTION_TREE;
  }

  setDeactivationGroupId(_value) {
    throw new Error("Cannot set deactivationGroupId on ActionTreeModel");
  }

  left() {
    return this.enterActions.reduce((a, b) => a.position.x < b.position.x ? a : b).position.x;
  }

  right() {
    return this.exitActions.reduce((a, b) => a.position.x > b.position.x ? a : b).position.x;
  }

  scale() {
    return subTreeNodeSize.width / (this.right() - this.left());
  }

  getChildActionById(id) {
    const result = this.getNonSubtreeChildActionById(id);
    if (result) return result;
    return this.getChildSubTreeActionById(id);
  }

  getNonSubtreeChildActionById(id) {
    return this.nonSubTreeActions.find(a => a.$modelId === id);
  }

  getChildSubTreeActionById(id) {
    return this.subtreeActions.find(a => a.$modelId === id);
  }

  isChildAction(action) {
    return this.nonSubTreeActions.includes(action) || this.subtreeActions.some(actionTree => actionTree === action);
  }

  searchActionRecursive(id) {
    const action = this.getChildActionById(id);
    if (action) return action;

    for (const subTree of this.subtreeActions) {
      const action = subTree.searchActionRecursive(id);
      if (action) return action;
    }

    return null;
  }

  get parentTree() {
    if (this.activeParentModelId) {
      return this.treeAccess.getTreeById(this.activeParentModelId);
    }

    return null;
  }
  /**
   * Returns the subtree path that leads to the assigned action.
   * @param action The action to find the path to.
   */


  findHierarchy(action) {
    let currentAction = action;
    let currentParent = getTreeParent(action);
    const path = [];

    while (currentParent) {
      path.push(currentParent);
      currentAction = currentParent;
      currentParent = getTreeParent(currentAction);
    }

    return path.reverse();
  }

  get allActions() {
    return [...this.nonSubTreeActions, ...this.subtreeActions];
  }

  get treePropertiesAction() {
    return this.nonSubTreeActions.find(a => a instanceof TreePropertiesActionModel);
  }

  get enterActions() {
    return this.nonSubTreeActions.filter(a => a instanceof TreeEnterActionModel).sort((a, b) => a.position.y - b.position.y);
  }

  get exitActions() {
    return this.nonSubTreeActions.filter(a => a instanceof TreeExitActionModel).sort((a, b) => a.position.y - b.position.y);
  }

  get subtreeActions() {
    return this.treeAccess.getSubTreesForActionTree(this);
  }

  get receiveQuestActions() {
    return this.nonSubTreeActions.filter(a => a instanceof ReceiveQuestActionModel);
  }

  get receiveTaskActions() {
    return this.nonSubTreeActions.filter(a => a instanceof ReceiveTaskActionModel);
  }

  get setTagActions() {
    return this.nonSubTreeActions.filter(a => a instanceof SetTagActionModel);
  }

  get setVariableActions() {
    return this.nonSubTreeActions.filter(a => a instanceof SetVariableActionModel);
  }

  get calculateVariableActions() {
    return this.nonSubTreeActions.filter(a => a instanceof CalculateVariableActionModel);
  }

  get interactionTriggerActions() {
    return this.nonSubTreeActions.filter(a => a instanceof InteractionTriggerActionModel);
  }

  get locationTriggerActions() {
    return this.nonSubTreeActions.filter(a => a instanceof LocationTriggerActionModel);
  }

  get copyAwarenessIntoVariableActions() {
    return this.nonSubTreeActions.filter(a => a instanceof CopyAwarenessIntoVariableActionModel);
  }

  get unsortedTreeParameterActions() {
    return this.nonSubTreeActions.filter(a => a instanceof TreeParamterActionModel);
  }

  treeParameterActions(type) {
    let treeParameterActions = this.unsortedTreeParameterActions;

    if (type !== undefined) {
      treeParameterActions = treeParameterActions.filter(a => a.value.$modelType === type);
    } // Sort by position of parameter nodes


    return treeParameterActions.sort((a, b) => a.position.x - b.position.x + (a.position.y - b.position.y));
  }
  /**
  * Returns allQuests Array with all quests in scope of given action tree,
  * searching up the tree hierarchy recursively for all quests defined in StartQuest nodes
  *
  * @param rootActionTree: Tree to search for quests
  * @param allQuests: Array that the found quests will be added to
  */


  quests(rootActionTree, allQuests = []) {
    this.receiveQuestActions.filter(a => !a.global).forEach(a => allQuests.push(a)); // If we reached a module root, return the quests we have collected so far

    if (this.type === ActionTreeType.ModuleRoot) return allQuests;

    if (this != rootActionTree) {
      const parentTree = getTreeParent(this);
      parentTree.quests(rootActionTree, allQuests); // Recurse with tree parent
    } else {
      rootActionTree.globalQuests(allQuests);
    }

    return allQuests;
  }

  questForId(id) {
    return this.allQuests().find(a => a.$modelId === id);
  }

  globalQuests(allQuests) {
    this.receiveQuestActions.filter(a => a.global).forEach(a => allQuests.push(a));
    this.subtreeActions.forEach(sub => sub.globalQuests(allQuests));
    return allQuests;
  }

  allQuests(allQuests = []) {
    this.receiveQuestActions.forEach(a => allQuests.push(a));
    this.subtreeActions.forEach(sub => sub.allQuests(allQuests));
    return allQuests;
  }

  tasks(questId, allTasks = []) {
    if (!questId) return allTasks;
    this.receiveTaskActions.filter(a => a.questId === questId).forEach(a => allTasks.push(a));
    this.subtreeActions.forEach(sub => sub.tasks(questId, allTasks));
    return allTasks;
  }

  taskForId(id) {
    return this.allTasks().find(a => a.$modelId === id);
  }

  allTasks(allTasks = []) {
    this.receiveTaskActions.forEach(a => allTasks.push(a));
    this.subtreeActions.forEach(sub => sub.allTasks(allTasks));
    return allTasks;
  }

  globalTags(allTags = new Set()) {
    this.setTagActions.forEach(a => allTags.add(a.tag));
    this.subtreeActions.forEach(sub => sub.globalTags(allTags));
    return allTags;
  }

  variableNames(scope, allVariableNames = new Set()) {
    allVariableNames.add("");
    this.setVariableActions.filter(a => a.scope === scope).forEach(a => allVariableNames.add(a.name));
    this.calculateVariableActions.filter(a => a.scope === scope).forEach(a => allVariableNames.add(a.variableResult));

    if (scope === ActionScope.Global) {
      this.copyAwarenessIntoVariableActions.forEach(c => allVariableNames.add(c.name));
      this.subtreeActions.forEach(sub => sub.variableNames(scope, allVariableNames));
    }

    return allVariableNames;
  }

  variableValues(name, scope, all = new Set()) {
    all.add("");
    this.setVariableActions.filter(a => a.scope === scope && a.name === name).forEach(a => all.add(a.value));

    if (scope === ActionScope.Global) {
      this.subtreeActions.forEach(sub => sub.variableValues(name, scope, all));
    }

    return all;
  }

  scopedName(name, scope, treeScopeContext) {
    if (scope === ActionScope.Global) return name;
    return getTreeParent(treeScopeContext).$modelId + "_" + name;
  }

  removeNonSubtreeAction(action) {
    if (action instanceof ActionTreeModel) throw Error("Cannot remove ActionTreeModel with removeNonSubtreeAction");
    const actionIndex = this.nonSubTreeActions.indexOf(action);
    if (actionIndex === -1) throw Error("removeNonSubtreeAction(): Action not found");

    if (action instanceof TreePropertiesActionModel) {
      return; // The properties node cannot be deleted
    }

    if (action instanceof TreeEnterActionModel && this.enterActions.length === 1) {
      return; // We need at least one entry to compute the tree scale
    }

    if (action instanceof TreeExitActionModel && this.exitActions.length === 1) {
      return; // We need at least one exit to compute the tree scale
    }

    this.nonSubTreeActions.splice(actionIndex, 1);
  }

  addNonSubtreeAction(action) {
    if (action instanceof ActionTreeModel) throw Error("Cannot add ActionTreeModel with addNonSubtreeAction");
    this.nonSubTreeActions.push(action);
  }

  resetPositionAndCleanParameters() {
    this.position.setX(0);
    this.position.setY(0);
    this.treeParameterActions().forEach(p => p.value.clean());
  }

  cloneWithNewIds(newType) {
    const allNewTrees = new Array();
    const copy = this.recursivelyCreateClones(newType, allNewTrees);
    const originalToCopyActionIdsMap = ActionTreeModel.generateActionIdsInCopyMap(this, copy);
    copy.fixActionIdsInCopy(originalToCopyActionIdsMap);
    copy.exitActions.forEach(exitAction => {
      exitAction.subTreeExit.setNextActions([]);
    });
    return {
      copy,
      allNewTrees
    };
  }

  recursivelyCreateClones(newType, allNewTrees) {
    const copy = clone(this);
    copy.setParentModelId(null);
    copy.setType(newType);

    copy.treeAccess.getSubTreesForActionTree = parent => allNewTrees.filter(child => child.activeParentModelId === parent.$modelId);

    allNewTrees.push(copy);
    const subtreeCloneResults = this.subtreeActions.map(subtree => subtree.recursivelyCreateClones(ActionTreeType.SubTree, allNewTrees));

    for (const subtreeClone of subtreeCloneResults) {
      subtreeClone.setParentModelId(copy.$modelId);
    }

    return copy;
  }

  static generateActionIdsInCopyMap(original, copy, originalToCopyActionIdsMap = new Map()) {
    original.allActions.forEach((originalAction, index) => {
      const copiedAction = copy.allActions[index];
      originalToCopyActionIdsMap.set(originalAction.$modelId, copiedAction.$modelId);

      if (copiedAction instanceof ActionTreeModel) {
        ActionTreeModel.generateActionIdsInCopyMap(originalAction, copiedAction, originalToCopyActionIdsMap);
      }
    });
    return originalToCopyActionIdsMap;
  }

  fixActionIdsInCopy(originalToCopyActionIdsMap) {
    this.allActions.forEach(copiedAction => {
      copiedAction.exits().forEach(copiedExit => {
        copiedExit.setNextActions(copiedExit.nextActions.map(next => originalToCopyActionIdsMap.get(next)));
      });

      if (copiedAction instanceof FinishQuestActionModel || copiedAction instanceof AbortQuestActionModel || copiedAction instanceof ReceiveTaskActionModel || copiedAction instanceof FinishTaskActionModel || copiedAction instanceof AbortTaskActionModel) {
        if (originalToCopyActionIdsMap.has(copiedAction.questId)) {
          copiedAction.setQuestId(originalToCopyActionIdsMap.get(copiedAction.questId));
        }
      }

      if (copiedAction instanceof FinishTaskActionModel || copiedAction instanceof AbortTaskActionModel) {
        if (originalToCopyActionIdsMap.has(copiedAction.taskId)) {
          copiedAction.setTaskId(originalToCopyActionIdsMap.get(copiedAction.taskId));
        }
      }
    });
    this.subtreeActions.forEach(subtree => {
      subtree.unsortedTreeParameterActions.forEach(treeParameterAction => {
        if (treeParameterAction.value instanceof QuestIdValueModel) {
          if (originalToCopyActionIdsMap.has(treeParameterAction.value.value)) {
            treeParameterAction.value.setValue(originalToCopyActionIdsMap.get(treeParameterAction.value.value));
          }
        }
      });
      subtree.fixActionIdsInCopy(originalToCopyActionIdsMap);
    });
  }
  /**
   * Returns true if the data for this action is complete.
   */


  get isDataComplete() {
    if (!this.areActionsValid) return false;
    return this.areParametersSet;
  }
  /**
   * Returns true if all containing actions are valid.
   */


  get areActionsValid() {
    return this.allActions.every(action => action.isDataComplete);
  }
  /**
   * Returns true if all {@link TreeParamterActionModel} are set.
   */


  get areParametersSet() {
    return this.treeParameterActions().every(parameter => parameter.allowBlankValue || parameter.isValueSet());
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "parentTree", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "parentTree"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "allActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "allActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "treePropertiesAction", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "treePropertiesAction"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "enterActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "enterActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "exitActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "exitActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "subtreeActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "subtreeActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "receiveQuestActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "receiveQuestActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "receiveTaskActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "receiveTaskActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setTagActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "setTagActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setVariableActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "setVariableActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "calculateVariableActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "calculateVariableActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "interactionTriggerActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "interactionTriggerActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "locationTriggerActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "locationTriggerActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "copyAwarenessIntoVariableActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "copyAwarenessIntoVariableActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "unsortedTreeParameterActions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "unsortedTreeParameterActions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "removeNonSubtreeAction", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "removeNonSubtreeAction"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "addNonSubtreeAction", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "addNonSubtreeAction"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "resetPositionAndCleanParameters", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "resetPositionAndCleanParameters"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "cloneWithNewIds", [modelAction], Object.getOwnPropertyDescriptor(_class2.prototype, "cloneWithNewIds"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isDataComplete", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "isDataComplete"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "areActionsValid", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "areActionsValid"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "areParametersSet", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "areParametersSet"), _class2.prototype)), _class2)) || _class);

/**
 * Returns the directly enclosing ActionTreeModel of the actionModel.
 * 
 * - ActionTreeModel <--  this will be the return value for *any* of the following child elements as `actionModel`
 *   - ActionTreeModel
 *   - ActionModel
 *   - ActionModel
 * 
 * Note that ActionTreeModels are ActionModels too.
 */
export function getTreeParent(actionModel) {
  // If actionModel is an ActionTreeModel, we use ActionTreeModel.parentTree to get to the parent.
  // (Since that relationship is not a part of the mobx-keystone parent-child tree hierarchy we cannot use getParent().)
  if (actionModel instanceof ActionTreeModel) {
    return actionModel.parentTree;
  } // Any other actionModel is a hierarchical child of an ActionTreeModel, or more specifically, a direct child of the
  // array ActionTreeModel.nonSubTreeActions.
  // If we call getParent() once, it returns that array; and if we call it again, it will return the ActionTreeModel.


  return getParent(getParent(actionModel));
}
/**
 * If element is an ActionModel: Returns its directly enclosing ActionTreeModel.
 * If element is not an ActionModel: First go to the enclosing ActionModel, then return its directily enclosing ActionTreeModel.
 * Returns the directly enclosing ActionTreeModel of the actionModel.
 * 
 * - ActionTreeModel <--  this will be the return value for *any* of the following child elements as `element`
 *   - ActionTreeModel
 *     - AnimationElementValueModel
 *       - AnimationElementReferenceModel
 *   - ActionModel
 *     - AnimationElementReferenceModel
 * 
 * Note that ActionTreeModels are ActionModels too.
 */

export function getTreeParentOfClosestActionModel(element, skipTreeParameterActionModel) {
  return getTreeParent(getClosestActionModel(element, skipTreeParameterActionModel));
}
/**
 * If element is an ActionModel: Just return it.
 * If element is not an ActionModel: Return its enclosing ActionModel.
 * 
 * Note that ActionTreeModels are ActionModels too.
*/

function getClosestActionModel(element, skipTreeParameterActionModel) {
  while (true) {
    if (element.isActionModel && (!skipTreeParameterActionModel || !(element instanceof TreeParamterActionModel))) {
      return element;
    }

    element = getParent(element);

    if (!element) {
      throw new Error("Couldn't find an ActionModel that is the parent of element in the hierachy.");
    }
  }
}